// @ts-nocheck
import { Dayjs } from 'dayjs';

import { ColorMode } from 'theme';

import { IndicatorManager } from './IndicatorManager';
import { HiroChartDatafeedIntfc } from 'types/hiro';
import { dayjs, ET, preMarketOpen } from 'util/shared/date';
import { filterHiroAlerts } from 'util/hiro';
import {
  HiroAlertCategory,
  HiroAlertCategoryFromAlertCategory,
  AlertData,
} from 'types';

export const REFETCH_ALERTS_INTERVAL_MS = 20_000;

export class AlertsManager extends IndicatorManager<HiroAlertCategory> {
  alertsFromCategoryFromDateFromSym: Record<
    string,
    Record<number, Record<HiroAlertCategory, AlertData[]>>
  >;

  timeOfTodaysLastFetch?: Dayjs;

  setHiroAlerts: (v: AlertCategory[]) => void;

  constructor(
    datafeed: HiroChartDatafeedIntfc,
    setHiroAlerts: (v: AlertCategory[]) => void,
  ) {
    super(datafeed);
    this.alertsFromCategoryFromDateFromSym = {};
    this.setHiroAlerts = setHiroAlerts;
  }

  async fetchAssets(dates: Dayjs[]) {
    const beginningOfDay = preMarketOpen();
    const now = dayjs();

    const datesToFetchFromSym: Record<string, Set<number>> = {};
    for (const sym of Object.keys(this.artifactsFromKeyFromSym)) {
      const alertsFromCategoryFromDate =
        (this.alertsFromCategoryFromDateFromSym[sym] =
          this.alertsFromCategoryFromDateFromSym[sym] ?? {});

      const datesToFetch = (datesToFetchFromSym[sym] =
        datesToFetchFromSym[sym] ?? new Set());
      for (const date of dates.filter(
        (d) =>
          !(d.valueOf() in alertsFromCategoryFromDate) ||
          (beginningOfDay <= d &&
            (this.timeOfTodaysLastFetch == null ||
              now.valueOf() - this.timeOfTodaysLastFetch.valueOf() >
                REFETCH_ALERTS_INTERVAL_MS - 1_000)),
      )) {
        datesToFetch.add(date.valueOf());
      }
    }

    await Promise.all(
      Object.entries(datesToFetchFromSym).flatMap(([sym, datesToFetch]) =>
        Array.from(datesToFetch).map((ts: number): Promise<void> => {
          const dateToFetch = dayjs(ts).tz(ET);
          const reqBegOfDay = dateToFetch;
          const reqEndOfDay = dateToFetch.add(1, 'day');
          return this.datafeed
            .fetchAPIWithLog<
              AlertData[]
            >(`v1/alerts?syms=${encodeURIComponent(sym)}&start=${reqBegOfDay.format('YYYY-MM-DD')}&end=${reqEndOfDay.format('YYYY-MM-DD')}`)
            .then((alerts: AlertData[]) => {
              if (dateToFetch >= beginningOfDay) {
                this.timeOfTodaysLastFetch = now;
                this.setHiroAlerts(filterHiroAlerts(alerts));
              }
              const alertsFromCategoryFromDate =
                (this.alertsFromCategoryFromDateFromSym[sym] =
                  this.alertsFromCategoryFromDateFromSym[sym] ?? {});
              const alertsFromCategory = (alertsFromCategoryFromDate[ts] =
                alertsFromCategoryFromDate[ts] ?? {});
              for (const alert of alerts) {
                const hiroAlertCategory = HiroAlertCategoryFromAlertCategory[
                  alert.category
                ] as string;
                if (hiroAlertCategory) {
                  const ary = (alertsFromCategory[hiroAlertCategory] =
                    alertsFromCategory[hiroAlertCategory] ?? []);
                  ary.push(alert);
                }
              }
            });
        }),
      ),
    );
  }

  async createArtifacts(dates: Dayjs[]) {
    await Promise.all(
      Object.entries(this.artifactsFromKeyFromSym).flatMap(
        ([sym, artifactsFromKey]) =>
          Object.entries(artifactsFromKey).flatMap(
            ([alertCategory, artifacts]) => {
              if (!artifacts.enabled) {
                return;
              }
              return dates.flatMap((d) => {
                if (artifacts.entityIdsFromDayStartMs[d.valueOf()]?.length) {
                  return;
                }
                const alerts: AlertData[] | undefined =
                  this.alertsFromCategoryFromDateFromSym[sym]?.[d.valueOf()]?.[
                    alertCategory
                  ];

                if (alerts == null) {
                  return;
                }

                const chart =
                  this.datafeed.chartingLibraryWidget?.activeChart();
                if (chart == null) {
                  return;
                }

                const entityIds = (artifacts.entityIdsFromDayStartMs[
                  d.valueOf()
                ] = artifacts.entityIdsFromDayStartMs[d.valueOf()] ?? []);

                const text = `${alertCategory.replace(/positive|negative/gi, '')}   `;

                return Promise.all(
                  alerts.map((alert) =>
                    chart
                      .createShape(
                        { time: dayjs(alert.alert_time).unix() },
                        {
                          shape: 'vertical_line',
                          showPrice: false,
                          lock: true,
                          vertLabelsAlign: 'bottom',
                          disableSave: true,
                          disableUndo: true,
                          text,
                          textAlignment: 'Top',
                          showLabel: true,
                          overrides: {
                            showLabel: true,
                            text,
                            textAlignment: 'Top',
                            vertLabelsAlign: 'bottom',
                            showTime: false,
                            lineWidth: 0.5,
                            linecolor:
                              this.datafeed.theme?.colorMode == ColorMode.LIGHT
                                ? 'rgba(10,143,88,0.25)'
                                : 'rgba(108,245,187,0.25)',
                            textcolor:
                              this.datafeed.theme?.colorMode == ColorMode.LIGHT
                                ? 'rgba(26,26,26,0.80)'
                                : 'rgba(219,219,219,0.80)',
                          },
                        },
                      )
                      .then((id) => {
                        entityIds.push(id);
                      }),
                  ),
                );
              });
            },
          ),
      ),
    );
  }
}
